import React, { useState, useEffect } from "react";
import { DatePicker, message, Tooltip, TimePicker, Select } from "antd";
import moment from "moment";
import DataParser from "../../../utlits/Dataparser";
import PhyicalYearService from "../../../Services/PhyicalYearService";
import LeaveApplicationService from "../../../Services/LeaveApplicationService";
import LeavetypeService from "../../../Services/PolicyServices";
import PastDaysService from "../../../Services/PastDaysService";
import SocketService from "../../../Services/SocketService";

const { Option } = Select;

const AddLeaves = (props) => {
    const { id, labelledby, getLeaveApplicationsAll, preSelectedLeaveDate } = props;

    const [leaveReason, setLeaveReason] = useState("");
    const [leaveType, setLeaveType] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [numberOfDays, setNumberOfDays] = useState(0);
    const [leavesDates, setLeavesDates] = useState([]);
    const [dataSubmitting, setDataSubmitting] = useState(false);
    const [leavePolicyList, setLeavePolicyList] = useState([]);
    const [leaveSession, setLeaveSession] = useState("");
    const [userLeaves, setUserLeaves] = useState([]);
    const [pastDaysLimit, setPastDaysLimit] = useState(0);

    const [leaveMode, setLeaveMode] = useState("full");
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [halfDayType, setHalfDayType] = useState("1st Half");
    const [totalTimeDisplay, setTotalTimeDisplay] = useState("");

    const companyId = DataParser.CompanyId();
    const userId = DataParser.UserId();

    useEffect(() => {
        LeavetypeService.getUserLeavePolicy(companyId, userId)
            .then((res) => {
                if (Array.isArray(res.data)) {
                    setLeavePolicyList(res.data);
                } else if (res.data?.success && Array.isArray(res.data.data)) {
                    setLeavePolicyList(res.data.data);
                } else {
                    setLeavePolicyList([]);
                }
            })
            .catch((err) => console.error("❌ Error fetching user leave policies:", err));
    }, [companyId, userId]);

    useEffect(() => {
        PhyicalYearService.getPhysicalYear(companyId).then((res) => {
            const data = res.data.Settings?.[0]?.id || "";
            setLeaveSession(data);
        });
    }, [companyId]);

    useEffect(() => {
        PastDaysService.getPastDays(companyId).then((res) => {
            const data = res.data || [];
            if (Array.isArray(data) && data.length > 0) {
                setPastDaysLimit(data[0].days);
            } else {
                setPastDaysLimit(0);
            }
        }).catch(err => console.error("Error fetching past days:", err));
    }, [companyId]);

    useEffect(() => {
        const fetchUserLeaves = async () => {
            try {
                const res = await LeaveApplicationService.getLeaveApplicationUser(userId, companyId, {});
                const leaves = res.data?.leave_applications || [];
                setUserLeaves(leaves);
            } catch (err) {
                console.error("❌ Error fetching user leaves:", err);
                setUserLeaves([]);
            }
        };
        if (userId && companyId) fetchUserLeaves();
    }, [userId, companyId]);

    useEffect(() => {
        if (preSelectedLeaveDate) {
            const d = moment(preSelectedLeaveDate);
            setFromDate(d);
            setToDate(d);
        }
    }, [preSelectedLeaveDate]);

    useEffect(() => {
        SocketService.connect();
        const handleLeaveUpdate = (data) => {
            if (String(data.company_id) === String(companyId) && String(data.user_id) === String(userId)) {
                LeavetypeService.getUserLeavePolicy(companyId, userId).then((res) => {
                    const payload = res.data;
                    if (Array.isArray(payload)) setLeavePolicyList(payload);
                    else if (payload?.success && Array.isArray(payload.data)) setLeavePolicyList(payload.data);
                }).catch(err => console.error(err));
                LeaveApplicationService.getLeaveApplicationUser(userId, companyId, {}).then((res) => {
                    setUserLeaves(res.data?.leave_applications || []);
                }).catch(err => console.error(err));
            }
        };
        SocketService.on("leave_update", handleLeaveUpdate);
        return () => SocketService.off("leave_update", handleLeaveUpdate);
    }, [companyId, userId]);

    const selectedPolicy = leavePolicyList.find((p) => p.leave_policy_id == leaveType || p.policy_id == leaveType);

    const isAlternateOff = (policyDay, dateInput) => {
        if (!policyDay.active) return true;
        if (!policyDay.alternate || !policyDay.alternate_type) return false;
        const date = moment(dateInput, ["YYYY-MM-DD", "DD-MM-YYYY", "MM-DD-YYYY"]);
        const baseDate = moment(policyDay.alternate, ["YYYY-MM-DD", "DD-MM-YYYY", "MM-DD-YYYY"]);
        if (!date.isValid() || !baseDate.isValid() || date.isBefore(baseDate)) return false;
        if (date.format('dddd').toLowerCase() !== baseDate.format('dddd').toLowerCase()) return false;
        const diffWeeks = date.diff(baseDate, "weeks");
        const cycleMap = { alternate: 2, bialternate: 3, trialternate: 4, "monthly alternate": 5 };
        const cycle = cycleMap[policyDay.alternate_type?.toLowerCase()] || 2;
        const status = policyDay.status || 'off';
        return status === 'off' ? (diffWeeks % cycle === 0) : (diffWeeks % cycle !== 0);
    };

    const isOffOrHoliday = (mDate) => {
        if (!selectedPolicy) return false;
        const d = mDate.format("YYYY-MM-DD");
        const holidays = selectedPolicy.holidays || [];
        if (holidays.includes(d)) return true;
        if (selectedPolicy.attendance_days) {
            const dayName = mDate.format("dddd").toLowerCase();
            const policyDay = selectedPolicy.attendance_days.find(p => p.day.toLowerCase() === dayName);
            if (policyDay) {
                if (!policyDay.active) return true;
                if (isAlternateOff(policyDay, mDate)) return true;
            }
        }
        const weekOffs = (selectedPolicy.off_days || [0, 6]).map((day) => Number(day) === 7 ? 0 : Number(day));
        return weekOffs.includes(mDate.day());
    };

    const getMaxAllowedToDate = (fromDate) => {
        if (!fromDate || !selectedPolicy) return null;
        const maxConsec = Math.min(Number(selectedPolicy.remaining_leaves) || 0, Number(selectedPolicy.consecutive_allowed) || 1);
        let workingDaysCount = 1, cur = moment(fromDate).clone().startOf("day");
        while (workingDaysCount < maxConsec) {
            cur.add(1, "day");
            if (!isOffOrHoliday(cur)) workingDaysCount++;
        }
        return cur;
    };

    const getAllowedDateRange = () => {
        if (!selectedPolicy) return [null, null];
        const sessionStart = moment(selectedPolicy.leave_session?.start_date), sessionEnd = moment(selectedPolicy.leave_session?.end_date);
        let start = moment().startOf("day"), validDaysCount = 0;
        while (validDaysCount < pastDaysLimit) {
            start.subtract(1, "day");
            if (!isOffOrHoliday(start)) validDaysCount++;
        }
        if (start.isBefore(sessionStart)) start = sessionStart.clone();
        return [start, sessionEnd];
    };

    const [minDate, maxDate] = getAllowedDateRange();

    useEffect(() => {
        if (!fromDate || (leaveMode === "full" && !toDate)) { setLeavesDates([]); setNumberOfDays(0); return; }
        const start = moment(fromDate).startOf("day");
        if (leaveMode === "half") {
            setLeavesDates([start.format("YYYY-MM-DD")]);
            setNumberOfDays(0.5);
            if (selectedPolicy?.attendance_days) {
                const dayPolicy = selectedPolicy.attendance_days.find(d => d.day.toLowerCase() === start.format("dddd").toLowerCase());
                if (dayPolicy?.active) {
                    const getMin = (t) => { const m = moment.isMoment(t) ? t : moment(t, ["HH:mm", "h:mm A"]); return m.hours() * 60 + m.minutes(); };
                    const pStart = getMin(dayPolicy.start), pEnd = getMin(dayPolicy.end);
                    let lunch = 0; if (dayPolicy.lunch_times) dayPolicy.lunch_times.forEach(lt => { lunch += (getMin(lt.end) - getMin(lt.start)); });
                    const halfMin = ((pEnd - pStart) - lunch) / 2;
                    setTotalTimeDisplay(`${Math.floor(halfMin / 60)}h ${Math.round(halfMin % 60)}m`);
                    let sTime = startTime;
                    if (!sTime) {
                        if (halfDayType === "1st Half") sTime = moment(dayPolicy.start, ["HH:mm", "h:mm A"]);
                        else sTime = moment().startOf('day').add(pStart + halfMin + lunch, 'minutes');
                        setStartTime(sTime);
                    }
                    if (sTime) {
                        let overlap = 0; if (dayPolicy.lunch_times) dayPolicy.lunch_times.forEach(lt => {
                            const lS = getMin(lt.start), lE = getMin(lt.end), oS = Math.max(getMin(sTime), lS), oE = Math.min(getMin(sTime) + halfMin + overlap, lE);
                            if (oE > oS) overlap += (oE - oS);
                        });
                        setEndTime(sTime.clone().add(halfMin + overlap, 'minutes'));
                    }
                }
            } else { setTotalTimeDisplay(""); setEndTime(null); }
            return;
        }
        const end = moment(toDate).startOf("day");
        if (end.isBefore(start)) { setLeavesDates([]); setNumberOfDays(0); return; }
        const dates = []; let cur = start.clone();
        while (!cur.isAfter(end)) { if (!isOffOrHoliday(cur)) dates.push(cur.format("YYYY-MM-DD")); cur.add(1, "day"); }
        setLeavesDates(dates); setNumberOfDays(dates.length);
    }, [fromDate, toDate, selectedPolicy, leaveMode, startTime, endTime, halfDayType]);

    const disabledFromDate = (c) => {
        if (!selectedPolicy || !minDate || !maxDate) return false;
        if (preSelectedLeaveDate) return !c.isSame(preSelectedLeaveDate, 'day');
        return c < minDate.clone().startOf("day") || c > maxDate.clone().endOf("day") || isOffOrHoliday(c);
    };

    const disabledToDate = (c) => {
        if (!selectedPolicy || !fromDate) return false;
        if (preSelectedLeaveDate) return !c.isSame(preSelectedLeaveDate, 'day');
        const fd = moment(fromDate).startOf("day"), maxAllowed = getMaxAllowedToDate(fd);
        return c.isBefore(fd) || (maxAllowed && c.isAfter(maxAllowed)) || isOffOrHoliday(c);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (leaveMode === "full" && (!fromDate || !toDate)) { message.error("Select dates."); return; }
        if (leaveMode === "half" && (!fromDate || !startTime)) { message.error("Select date and time."); return; }
        if (!selectedPolicy || selectedPolicy.remaining_leaves <= 0) { message.error("No leaves."); return; }
        if (numberOfDays > selectedPolicy.remaining_leaves) { message.error("Limit exceeded."); return; }
        const overlapping = leavesDates.filter(d => (userLeaves || []).some(l => l?.empid === userId && (l?.status === 0 || l?.status === 1) && l.leavedates?.includes(d)));
        if (overlapping.length > 0) { message.error(`Overlap: ${overlapping.join(", ")}`); return; }

        setDataSubmitting(true);
        const body = {
            company_id: companyId, reason: leaveReason, empid: userId,
            from_date: moment(fromDate).format("YYYY-MM-DD"), to_date: leaveMode === "half" ? moment(fromDate).format("YYYY-MM-DD") : moment(toDate).format("YYYY-MM-DD"),
            totalleaves: numberOfDays, typ: selectedPolicy.leave_policy_id, leavesession: leaveSession, leavedates: leavesDates, leave_mode: leaveMode,
            half_day_type: leaveMode === "half" ? halfDayType : null, start_time: startTime ? moment(startTime).format("HH:mm") : null, end_time: endTime ? moment(endTime).format("HH:mm") : null,
        };
        LeaveApplicationService.applyLeave(body).then(res => {
            if (res.Good) { message.success("Applied!"); document.getElementById("closeLeave").click(); setLeaveReason(""); setLeaveType(null); setFromDate(null); setToDate(null); setNumberOfDays(0); getLeaveApplicationsAll(userId, companyId); }
            else message.error(res.data || "Error");
        }).finally(() => setDataSubmitting(false));
    };

    return (
        <div className="modal fade" id={id} tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                    <div className="modal-header border-0 pb-1">
                        <h5 className="modal-title fw-semibold" style={{ fontSize: "16px", color: "#1D1D1D" }}>Apply For Leave</h5>
                        <button type="button" className="btn-close shadow-none" id="closeLeave" data-bs-dismiss="modal" onClick={() => { setLeaveType(null); setLeaveReason(""); }} />
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body pt-2 pb-2">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className={`float-select-wrap ${leaveMode ? "has-value" : ""}`}>
                                        <label>Leave Mode* <Tooltip title="Full or Half Day leave." placement="top"><i className="fa fa-info-circle float-label-helper-icon" /></Tooltip></label>
                                        <Select value={leaveMode} onChange={val => { setLeaveMode(val); if (val === "full") { setStartTime(null); setEndTime(null); } setToDate(null); setNumberOfDays(0); setLeavesDates([]); }}>
                                            <Option value="full">Full Day</Option><Option value="half">Half Day</Option>
                                        </Select>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className={`float-select-wrap ${leaveType ? "has-value" : ""}`}>
                                        <label>Leave Type* <Tooltip title="Select type of leave." placement="top"><i className="fa fa-info-circle float-label-helper-icon" /></Tooltip></label>
                                        <Select placeholder="" value={leaveType || undefined} onChange={val => { setLeaveType(val); setFromDate(null); setToDate(null); setNumberOfDays(0); }}>
                                            {leavePolicyList.map(el => <Option key={el.leave_policy_id || el.policy_id} value={el.leave_policy_id || el.policy_id}>{el.leave_type_name}</Option>)}
                                        </Select>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className={`float-select-wrap ${fromDate ? "has-value" : ""}`}>
                                        <label>{leaveMode === "half" ? "Date*" : "Start Date*"} <Tooltip title="Starting date." placement="top"><i className="fa fa-info-circle float-label-helper-icon" /></Tooltip></label>
                                        <DatePicker disabledDate={disabledFromDate} value={fromDate ? moment(fromDate) : null} onChange={d => { setFromDate(d); setToDate(null); }} format="DD-MM-YYYY" style={{ width: "100%" }} allowClear={false} placeholder="" />
                                    </div>
                                </div>
                                {leaveMode === "full" && (
                                    <div className="col-md-6 mb-3">
                                        <div className={`float-select-wrap ${toDate ? "has-value" : ""}`}>
                                            <label>End Date* <Tooltip title="End date." placement="top"><i className="fa fa-info-circle float-label-helper-icon" /></Tooltip></label>
                                            <DatePicker disabledDate={disabledToDate} value={toDate ? moment(toDate) : null} onChange={setToDate} format="DD-MM-YYYY" disabled={!fromDate} style={{ width: "100%" }} allowClear={false} placeholder="" />
                                        </div>
                                    </div>
                                )}
                                {leaveMode === "half" && (
                                    <>
                                        <div className="col-md-6 mb-3">
                                            <div className={`float-select-wrap ${halfDayType ? "has-value" : ""}`}>
                                                <label>Slot Type* <Tooltip title="1st or 2nd half." placement="top"><i className="fa fa-info-circle float-label-helper-icon" /></Tooltip></label>
                                                <Select value={halfDayType} onChange={setHalfDayType}><Option value="1st Half">1st Half</Option><Option value="2nd Half">2nd Half</Option></Select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className={`float-select-wrap ${startTime ? "has-value" : ""}`}>
                                                <label>Start Time* <Tooltip title="Start time." placement="top"><i className="fa fa-info-circle float-label-helper-icon" /></Tooltip></label>
                                                <TimePicker value={startTime} onChange={setStartTime} format="h:mm a" use12Hours style={{ width: "100%" }} allowClear={false} placeholder="" />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className={`float-select-wrap ${endTime ? "has-value" : ""}`}>
                                                <label>End Time (Auto) <Tooltip title="Auto calculated." placement="top"><i className="fa fa-info-circle float-label-helper-icon" /></Tooltip></label>
                                                <TimePicker value={endTime} format="h:mm a" use12Hours style={{ width: "100%", backgroundColor: "#f8f9fa" }} disabled placeholder="" />
                                            </div>
                                        </div>
                                        {totalTimeDisplay && <div className="col-md-6 mb-3 d-flex align-items-center"><small className="text-muted">Duration: <strong>{totalTimeDisplay}</strong></small></div>}
                                    </>
                                )}
                                {numberOfDays > 0 && <div className="col-12 mb-3"><div className="p-2 rounded border d-flex justify-content-between align-items-center" style={{ backgroundColor: "#fafafa" }}><span className="text-secondary small fw-medium">Total Days:</span><span className="badge bg-primary px-3 py-2">{numberOfDays} Days</span></div></div>}
                                {selectedPolicy && <div className="col-12 mb-3"><div className="alert border-0 p-2 mb-0" style={{ backgroundColor: "#e6fff1", borderRadius: "8px" }}><div className="d-flex justify-content-between"><small style={{ color: "#00a651" }}><strong>Available:</strong> {selectedPolicy.remaining_leaves || 0} days</small><small style={{ color: "#00a651" }}><strong>Max Consecutive:</strong> {selectedPolicy.consecutive_allowed || 1} days</small></div></div></div>}
                                <div className="col-12 mb-1">
                                    <div className={`float-input-wrap ${leaveReason ? "has-value" : ""}`}>
                                        <textarea className="form-control" value={leaveReason} onChange={e => setLeaveReason(e.target.value)} rows={2} style={{ borderRadius: "6px", border: "1px solid #eff2f7" }} placeholder=" " />
                                        <label>Reason* <Tooltip title="Leave reason." placement="top"><i className="fa fa-info-circle float-label-helper-icon" /></Tooltip></label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer border-0 pt-0 justify-content-end">
                            <button type="submit" disabled={dataSubmitting} className="btn btn-primary waves-effect waves-light px-4" style={{ borderRadius: "8px", padding: "10px", fontWeight: "600", minWidth: "120px" }}>{dataSubmitting ? "Processing..." : "+ Apply Leave"}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddLeaves;
